import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    Paper,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SpinningModal from '../../components/ai-chat/SpinningModal';
import TaskButton from '../../components/TaskButtons/TaskButton';
import { styled } from '@mui/system';
import { v4 as uuidv4 } from 'uuid';
import { AzureKeyCredential, SearchClient } from '@azure/search-documents';

import userLogo from '../../assets/fig_CROP.png';
import agentLogo from '../../assets/fig_agent.png';

// ----------------------------------------------------------
// Azure Search and Public Endpoint configuration
// ----------------------------------------------------------
const AZURE_SEARCH_ENDPOINT = "https://figagent.search.windows.net";
const AZURE_SEARCH_API_KEY_1 = "e3co1Jd030qRUaFEFgPwXQeeBkorB13VQvJBEBatWXAzSeBXMA6p";
const AZURE_SEARCH_API_KEY_2 = "6TEAWySZrm8g9RECPSagkzMkiVL55X2fEZyRajqEuuAzSeAiY49D";
const FIG_COURSES_INDEX = "fig-courses";
const VECTOR_PRODMST_INDEX = "vector-prodmst";

const figCoursesClient = new SearchClient(
    AZURE_SEARCH_ENDPOINT,
    FIG_COURSES_INDEX,
    new AzureKeyCredential(AZURE_SEARCH_API_KEY_1)
);

const vectorProdmstClient = new SearchClient(
    AZURE_SEARCH_ENDPOINT,
    VECTOR_PRODMST_INDEX,
    new AzureKeyCredential(AZURE_SEARCH_API_KEY_2)
);

// ----------------------------------------------------------
// Styled Components
// ----------------------------------------------------------
const ChatWindow = styled(Paper)(({ theme }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    padding: theme.spacing(2),
}));

const ChatHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ChatContent = styled(Box)(({ theme }) => ({
    flex: 1,
    padding: theme.spacing(1),
    overflowY: 'auto',
    backgroundColor: '#ffffff',
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #ccc',
    marginBottom: theme.spacing(1),
}));

const ChatInput = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
}));

// ----------------------------------------------------------
// ProductivityChat Component
// ----------------------------------------------------------
const ProductivityChat = ({ onClose }) => {
    const [conversation, setConversation] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isThinking, setIsThinking] = useState(false);
    const [isDataModalOpen, setIsDataModalOpen] = useState(false);
    const [dataInput, setDataInput] = useState('');
    const [chatContext, setChatContext] = useState('');
    const chatContentRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (chatContentRef.current) {
            chatContentRef.current.scrollTop = chatContentRef.current.scrollHeight;
        }
    }, [conversation]);

    // ----------------------------------------------------------
    // Helper functions: Azure Search Data
    // ----------------------------------------------------------
    const getFigCoursesData = async (query) => {
        const results = [];
        try {
            const response = await figCoursesClient.search(query, {
                searchMode: "all",
                top: 5,
            });
            for await (const result of response.results) {
                results.push(result.document);
            }
        } catch (error) {
            console.error("Error fetching data from fig-courses:", error);
        }
        return results;
    };

    const getVectorProdmstData = async (query) => {
        const results = [];
        try {
            const response = await vectorProdmstClient.search(query, {
                searchMode: "all",
                top: 5,
            });
            for await (const result of response.results) {
                results.push(result.document);
            }
        } catch (error) {
            console.error("Error fetching data from vector-prodmst:", error);
        }
        return results;
    };

    const handleSend = async () => {
        if (isThinking) return;
        if (!inputValue.trim()) return;

        const userMessage = inputValue.trim();
        setInputValue('');

        // Add user's message to conversation
        const newConversation = [...conversation, { role: 'user', content: userMessage }];
        setConversation(newConversation);
        setIsThinking(true);

        try {
            // ----------------------------------------------------------
            // 1) Fetch data from your public endpoint (ai_usda_recipe)
            // ----------------------------------------------------------
            let apiData = '';
            try {
                const response = await axios.get('https://api.swancreate.com/api/llm/ai_usda_recipe');
                if (Array.isArray(response.data)) {
                    apiData = `Number of records: ${response.data.length}\nDetails:\n${JSON.stringify(response.data, null, 2)}`;
                } else if (typeof response.data === 'object') {
                    apiData = JSON.stringify(response.data, null, 2);
                } else {
                    apiData = String(response.data);
                }
            } catch (error) {
                console.error('Error fetching ai_usda_recipe data:', error);
            }

            // ----------------------------------------------------------
            // 2) Fetch data from Azure Search (fig-courses & vector-prodmst)
            // ----------------------------------------------------------
            let figCoursesData = [];
            let vectorProdmstData = [];
            try {
                figCoursesData = await getFigCoursesData(userMessage);
                vectorProdmstData = await getVectorProdmstData(userMessage);
            } catch (error) {
                console.error('Error fetching from Azure Search indexes:', error);
            }

            const coursesStr = JSON.stringify(figCoursesData, null, 2);
            const prodmstStr = JSON.stringify(vectorProdmstData, null, 2);

            // ----------------------------------------------------------
            // 3) Combine all data into a single context string
            // ----------------------------------------------------------
            let combinedContext = `API Table Data (ai_usda_recipe):\n${apiData}`;
            if (chatContext && chatContext.trim().length > 0) {
                combinedContext = `Additional Chat Context:\n${chatContext}\n\n${combinedContext}`;
            }
            combinedContext += `\n\nAzure Search (fig-courses):\n${coursesStr}\n`;
            combinedContext += `\nAzure Search (vector-prodmst):\n${prodmstStr}`;

            // ----------------------------------------------------------
            // 4) Construct final prompt by combining context and user message
            // ----------------------------------------------------------
            const finalPrompt = `Additional Context:
${combinedContext}

Human: ${userMessage}
Assistant:`;

            // ----------------------------------------------------------
            // 5) Send the prompt to the new AI endpoint
            // ----------------------------------------------------------
            const response = await axios.post(
                "https://aiendpoint.swancreate.com/api/ai/gpt",
                { prompt: finalPrompt },
                { headers: { "Content-Type": "application/json" } }
            );
            const assistantResponse = response.data.response || 'No response received.';

            // ----------------------------------------------------------
            // 6) Add the AI's response to the conversation
            // ----------------------------------------------------------
            setConversation((prev) => [...prev, { role: 'assistant', content: assistantResponse }]);
        } catch (error) {
            console.error('Error processing request:', error);
            setConversation((prev) => [
                ...prev,
                { role: 'assistant', content: 'Sorry, there was an error processing your request.' },
            ]);
        } finally {
            setIsThinking(false);
        }
    };

    // ----------------------------------------------------------
    // Dialog for adding additional context data at runtime
    // ----------------------------------------------------------
    const handleAddData = () => {
        if (!dataInput.trim()) return;
        setChatContext(dataInput.trim());
        setDataInput('');
        setIsDataModalOpen(false);
    };

    return (
        <Box sx={{ width: '100%', height: '100%' }}>
            <ChatWindow elevation={5}>
                <ChatHeader>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="h6" sx={{ fontSize: '22pt' }}>
                            Productivity Chat
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => setIsDataModalOpen(true)}
                            sx={{
                                backgroundColor: '#333',
                                color: '#fff',
                                textTransform: 'none',
                                '&:hover': { backgroundColor: '#555' },
                            }}
                        >
                            Add Chat Context Data
                        </Button>
                    </Box>
                    {onClose && (
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </ChatHeader>

                <ChatContent ref={chatContentRef}>
                    {conversation.map((msg, index) => (
                        <Box key={index} display="flex" alignItems="flex-start" mb={1}>
                            <Avatar
                                src={msg.role === 'assistant' ? agentLogo : userLogo}
                                alt={msg.role === 'assistant' ? 'Assistant' : 'User'}
                                sx={{ mr: 1, width: 40, height: 40 }}
                            />
                            <TaskButton content={msg.content} />
                        </Box>
                    ))}
                    {isThinking && (
                        <Box display="flex" alignItems="center" mb={1}>
                            <Avatar src={agentLogo} alt="Assistant" sx={{ mr: 1, width: 40, height: 40 }} />
                            <SpinningModal />
                        </Box>
                    )}
                </ChatContent>

                <ChatInput>
                    <TextField
                        inputRef={inputRef}
                        fullWidth
                        placeholder="Type your message..."
                        variant="outlined"
                        multiline
                        minRows={3}
                        maxRows={6}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSend();
                                if (inputRef.current) {
                                    inputRef.current.blur();
                                }
                            }
                        }}
                        sx={{
                            padding: 1,
                            fontSize: '16px',
                            lineHeight: 1.5,
                            backgroundColor: '#f9f9f9',
                            borderRadius: 1,
                        }}
                    />
                    <Button variant="contained" color="primary" sx={{ mt: 1, alignSelf: 'flex-end' }} onClick={handleSend}>
                        Send
                    </Button>
                </ChatInput>
            </ChatWindow>

            <Dialog
                open={isDataModalOpen}
                onClose={() => setIsDataModalOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Add Chat Context Data</DialogTitle>
                <DialogContent>
                    <TextField
                        value={dataInput}
                        onChange={(e) => setDataInput(e.target.value)}
                        placeholder="Enter chat context data..."
                        multiline
                        minRows={4}
                        variant="outlined"
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsDataModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleAddData} variant="contained" color="primary">
                        Add Data
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProductivityChat;
